import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import PostComments from './PostComments';
import FBPostComments from './FBPostComments';
import PlatformCapabilityBadge from './PlatformCapabilityBadge';
import { notify, confirmAsync } from '../utils/notify';
import './WeeklyPostGenerator.css';

// Statuses for which "Publish now" can promote a ScheduledPost to immediate
// publication. Mirrors PUBLISH_NOW_PROMOTABLE_STATUSES in
// backend/app/api/v1/posts.py.
const PUBLISH_NOW_PROMOTABLE_STATUSES = ['approved', 'queued'];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// `publishing` is included so a post being processed by the Celery worker
// (queued → publishing → published/partially_published/failed) stays
// visible in the Scheduled tab during the ~50s publish window. Without
// it the post would disappear from the list while the worker runs.
const SCHEDULED_STATUSES = ['approved', 'queued', 'publishing', 'published', 'partially_published', 'rejected', 'failed'];

// Statuses that indicate publishing has terminated — used by the
// publish-now polling loop to know when to stop refreshing.
const PUBLISH_TERMINAL_STATUSES = ['published', 'partially_published', 'failed'];

const PLATFORM_ICONS = {
  instagram: '📸',
  facebook: '📘',
  tiktok: '🎵',
};

const PLATFORM_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
};

/**
 * Parse a platform_results value into a user-friendly error description.
 * Backend format: "published" | "token_expired: ..." | "failed: ..." | "account_inactive" etc.
 */
const parsePlatformResult = (value) => {
  if (!value || value === 'published') return null;
  if (value === 'token_expired') return 'token_expired';
  if (value === 'account_inactive' || value === 'account_not_connected') return value;
  if (value === 'page_not_connected') return 'page_not_connected';
  if (value === 'token_invalid') return 'token_invalid';
  if (value === 'permission_missing') return 'permission_missing';
  if (value === 'needs_reconnection') return 'needs_reconnection';
  // Strip verbose prefix for cleaner display
  const cleaned = value
    .replace(/^token_expired:\s*/i, '')
    .replace(/^failed:\s*/i, '')
    .replace(/Token error:\s*/i, '');
  // Truncate long messages
  return cleaned.length > 120 ? cleaned.slice(0, 120) + '…' : cleaned;
};

function PostEditForm({ post, onSave, onCancel }) {
  const { t } = useTranslation();
  const [script, setScript] = useState(post.script_text || '');
  const [caption, setCaption] = useState(post.caption || '');
  const [hashtags, setHashtags] = useState(post.hashtags?.join(', ') || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      script_text: script,
      caption,
      hashtags: hashtags
        .split(',')
        .map((tag) => tag.trim().replace('#', ''))
        .filter(Boolean),
    });
  };

  return (
    <form className="edit-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label>{t('weeklyPosts.script')}</label>
        <textarea value={script} onChange={(e) => setScript(e.target.value)} rows={6} />
      </div>
      <div className="form-group">
        <label>{t('weeklyPosts.caption')}</label>
        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} />
      </div>
      <div className="form-group">
        <label>{t('weeklyPosts.hashtags')}</label>
        <input
          type="text"
          value={hashtags}
          onChange={(e) => setHashtags(e.target.value)}
          placeholder={t('weeklyPosts.hashtagsPlaceholder')}
        />
      </div>
      <div className="form-actions">
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-primary">
          {t('common.save')}
        </button>
      </div>
    </form>
  );
}

function Posts() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Tab state ──
  const [activeTab, setActiveTab] = useState('generate');

  // ── Generation tab state ──
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [regeneratingPost, setRegeneratingPost] = useState(null);
  const [generatingVideoPost, setGeneratingVideoPost] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [hasInstagram, setHasInstagram] = useState(false);
  const [hasTiktok, setHasTiktok] = useState(false);
  const [hasFacebook, setHasFacebook] = useState(false);
  const [hasVoiceClone, setHasVoiceClone] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState(['nuovi_utenti']);
  const [availableTargets, setAvailableTargets] = useState({});
  const [activeArchetype, setActiveArchetype] = useState(null); // {id, label_it, icon}

  // ── Scheduled tab state ──
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduledFilter, setScheduledFilter] = useState('all');
  const [commentsModal, setCommentsModal] = useState(null); // { postId, caption }
  const [commentCounts, setCommentCounts] = useState({}); // { postId: count }
  const [fbCommentsModal, setFbCommentsModal] = useState(null); // { postId, caption }
  const [fbCommentCounts, setFbCommentCounts] = useState({}); // { postId: count }
  const [editingCaption, setEditingCaption] = useState(null); // postId being edited
  const [captionText, setCaptionText] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);
  const [deletingPost, setDeletingPost] = useState(null); // postId being deleted
  const [publishingNowPost, setPublishingNowPost] = useState(null); // postId being promoted
  const [publishNotification, setPublishNotification] = useState(null); // { type: 'success'|'partial'|'error'|'info', message }
  const [retryingScheduling, setRetryingScheduling] = useState(null); // postId being re-scheduled
  const [capabilities, setCapabilities] = useState(null); // { platforms, connected, notes }

  useEffect(() => {
    checkPrerequisites();
    fetchCurrentBatch();
    fetchActiveArchetype();
    fetchTargets();
    fetchCapabilities();
  }, []);

  // Poll while batch is generating_content (LLM running in background)
  useEffect(() => {
    if (!batch || batch.status !== 'generating_content') return;

    const interval = setInterval(async () => {
      try {
        const response = await apiClient.get('/api/v1/posts/batch/current');
        if (response.data) {
          setBatch(response.data);
          // Stop polling once we leave generating_content
          if (response.data.status !== 'generating_content') {
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Error polling batch:', error);
      }
    }, 4000); // Poll every 4 seconds

    return () => clearInterval(interval);
  }, [batch?.status]);

  // Poll while batch is generating audio/video (after confirmation)
  useEffect(() => {
    if (!batch || !['confirmed', 'generating'].includes(batch.status)) {
      setGenerationProgress(null);
      return;
    }

    // Fetch progress immediately, then poll
    const fetchProgress = async () => {
      try {
        const [batchRes, progressRes] = await Promise.all([
          apiClient.get('/api/v1/posts/batch/current'),
          apiClient.get(`/api/v1/posts/batch/${batch.id}/generation-status`),
        ]);
        if (batchRes.data) {
          setBatch(batchRes.data);
        }
        if (progressRes.data) {
          setGenerationProgress(progressRes.data);
        }
        // Stop polling once generation is done or failed
        if (batchRes.data && !['confirmed', 'generating'].includes(batchRes.data.status)) {
          return true; // signal to stop
        }
      } catch (error) {
        console.error('Error polling generation progress:', error);
      }
      return false;
    };

    fetchProgress();

    const interval = setInterval(async () => {
      const shouldStop = await fetchProgress();
      if (shouldStop) clearInterval(interval);
    }, 8000); // Poll every 8 seconds (audio+video takes minutes)

    return () => clearInterval(interval);
  }, [batch?.status, batch?.id]);

  const checkPrerequisites = async () => {
    try {
      // Check Instagram
      const igResponse = await apiClient.get('/api/v1/posts/accounts');
      const activeIg = igResponse.data.find((acc) => acc.is_active);
      setHasInstagram(!!activeIg);

      // Check TikTok
      let hasTiktokConnected = false;
      try {
        const ttResponse = await apiClient.get('/api/v1/social/tiktok/account');
        hasTiktokConnected = !!ttResponse.data?.is_active;
        setHasTiktok(hasTiktokConnected);
      } catch {
        setHasTiktok(false);
      }

      // Check Facebook Page
      let hasFacebookConnected = false;
      try {
        const fbResponse = await apiClient.get('/api/v1/social/facebook/account');
        hasFacebookConnected = !!fbResponse.data?.is_active;
        setHasFacebook(hasFacebookConnected);
      } catch {
        setHasFacebook(false);
      }

      // Check Voice Clone
      const voiceResponse = await apiClient.get('/api/v1/voice-clone/status');
      setHasVoiceClone(voiceResponse.data.status === 'completed');

      // Auto-enable all connected platforms by default
      const connectedPlatforms = [];
      if (activeIg) connectedPlatforms.push('instagram');
      if (hasTiktokConnected) connectedPlatforms.push('tiktok');
      if (hasFacebookConnected) connectedPlatforms.push('facebook');
      setSelectedPlatforms((prev) => prev.length === 0 ? connectedPlatforms : prev);
    } catch (error) {
      console.error('Error checking prerequisites:', error);
    }
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms((prev) => {
      if (prev.includes(platform)) {
        // Prevent deselecting the last platform
        if (prev.length <= 1) return prev;
        return prev.filter((p) => p !== platform);
      }
      return [...prev, platform];
    });
  };

  const fetchActiveArchetype = async () => {
    try {
      const [archetypesRes, userToneRes] = await Promise.all([
        apiClient.get('/api/v1/tone-archetypes'),
        apiClient.get('/api/v1/users/me/tone-archetype'),
      ]);
      const userArchetypeId = userToneRes.data?.tone_archetype;
      if (userArchetypeId) {
        const found = archetypesRes.data.find((a) => a.id === userArchetypeId);
        if (found) setActiveArchetype(found);
      }
    } catch (error) {
      console.error('Error fetching active archetype:', error);
    }
  };

  const fetchTargets = async () => {
    try {
      const response = await apiClient.get('/api/v1/posts/targets');
      setAvailableTargets(response.data);
    } catch (error) {
      console.error('Error fetching targets:', error);
    }
  };

  const toggleTarget = (targetKey) => {
    setSelectedTargets((prev) => {
      if (prev.includes(targetKey)) {
        // Prevent deselecting the last target
        if (prev.length <= 1) return prev;
        return prev.filter((t) => t !== targetKey);
      }
      return [...prev, targetKey];
    });
  };

  const fetchCurrentBatch = async () => {
    try {
      const response = await apiClient.get('/api/v1/posts/batch/current');
      setBatch(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching batch:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Scheduled posts fetch ──
  const fetchScheduledPosts = useCallback(async () => {
    setScheduledLoading(true);
    try {
      const response = await apiClient.get('/api/v1/posts/posts');
      setScheduledPosts(response.data || []);

      // Fetch comment counts for published posts (IG + FB in parallel)
      try {
        const [igSummary, fbSummary] = await Promise.all([
          apiClient.get('/api/v1/comments/summary').catch(() => ({ data: [] })),
          apiClient.get('/api/v1/fb-comments/summary').catch(() => ({ data: [] })),
        ]);
        const igCounts = {};
        (igSummary.data || []).forEach((s) => { igCounts[s.post_id] = s.comment_count; });
        setCommentCounts(igCounts);
        const fbCounts = {};
        (fbSummary.data || []).forEach((s) => { fbCounts[s.post_id] = s.comment_count; });
        setFbCommentCounts(fbCounts);
      } catch { /* ignore if endpoints not ready */ }
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
    } finally {
      setScheduledLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'scheduled') {
      fetchScheduledPosts();
    }
  }, [activeTab, fetchScheduledPosts]);

  // Only show posts that have entered the scheduling pipeline (not drafts / generation-in-progress)
  const schedulablePosts = scheduledPosts.filter((post) =>
    SCHEDULED_STATUSES.includes(post.status)
  );

  // A post counts as "already published somewhere" when at least one
  // platform succeeded — used to keep partially-published posts visible
  // under the "Published" filter while a failed platform is being
  // retried (status transiently flips to queued/publishing).
  const hasAnyPublishedPlatform = (post) =>
    Object.values(post.platform_results || {}).some((r) => r === 'published');

  const filteredScheduledPosts = schedulablePosts.filter((post) => {
    if (scheduledFilter === 'all') return true;
    if (scheduledFilter === 'failed') return ['failed', 'audio_failed', 'video_failed', 'partially_published'].includes(post.status);
    if (scheduledFilter === 'published') {
      if (['published', 'partially_published'].includes(post.status)) return true;
      // A post being (re-)published keeps showing under "Published" as
      // long as it still carries an already-published platform — so a
      // single-platform retry never makes the post vanish from view.
      if (['queued', 'publishing'].includes(post.status)) return hasAnyPublishedPlatform(post);
      return false;
    }
    // Keep an in-flight (`publishing`) post visible under the "queued"
    // filter so the publish-now polling can update its pill in place.
    if (scheduledFilter === 'queued') return ['queued', 'publishing'].includes(post.status);
    return post.status === scheduledFilter;
  });

  const scheduledCount = schedulablePosts.length;

  const handleGenerateWeek = async () => {
    if (selectedPlatforms.length === 0) {
      notify.warning(t('weeklyPosts.selectAtLeastOnePlatform'));
      return;
    }
    setGenerating(true);
    try {
      const response = await apiClient.post('/api/v1/posts/generate-week', {
        target_platforms: selectedPlatforms,
        target_audiences: selectedTargets,
      });
      // Batch returns immediately in "generating_content" status.
      // The useEffect polling hook will pick up changes automatically.
      setBatch(response.data);
    } catch (error) {
      notify.error(error.response?.data?.detail || t('weeklyPosts.generateError'));
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirmBatch = async () => {
    if (!(await confirmAsync({ message: t('weeklyPosts.confirmMessage') }))) return;

    setConfirming(true);
    try {
      await apiClient.post(`/api/v1/posts/batch/${batch.id}/confirm`);
      fetchCurrentBatch();
    } catch (error) {
      notify.error(error.response?.data?.detail || t('weeklyPosts.confirmError'));
    } finally {
      setConfirming(false);
    }
  };

  const handleDeleteBatch = async () => {
    if (!(await confirmAsync({ message: t('weeklyPosts.deleteConfirm'), danger: true }))) return;
    try {
      await apiClient.delete(`/api/v1/posts/batch/${batch.id}`);
      setBatch(null);
    } catch (error) {
      notify.error(error.response?.data?.detail || t('weeklyPosts.deleteError'));
    }
  };

  const handleSaveEdit = async (postId, updates) => {
    try {
      await apiClient.put(`/api/v1/posts/posts/${postId}`, updates);
      fetchCurrentBatch();
      setEditingPost(null);
    } catch (error) {
      notify.error(error.response?.data?.detail || t('common.saveError'));
    }
  };

  const handleRegenerateAll = async () => {
    if (!(await confirmAsync({ message: t('weeklyPosts.regenerateAllConfirm') }))) return;

    setRegeneratingAll(true);
    try {
      await apiClient.post(`/api/v1/posts/batch/${batch.id}/regenerate`);
      // Batch goes back to generating_content — polling will pick it up
      fetchCurrentBatch();
    } catch (error) {
      notify.error(error.response?.data?.detail || t('weeklyPosts.regenerateError'));
    } finally {
      setRegeneratingAll(false);
    }
  };

  const handleRegenerateSingle = async (postId) => {
    if (!(await confirmAsync({ message: t('weeklyPosts.regenerateSingleConfirm') }))) return;

    setRegeneratingPost(postId);
    try {
      await apiClient.post(`/api/v1/posts/posts/${postId}/regenerate`);
      // Start polling for this post to finish regenerating
      const pollInterval = setInterval(async () => {
        try {
          const response = await apiClient.get('/api/v1/posts/batch/current');
          if (response.data) {
            setBatch(response.data);
            const updatedPost = response.data.posts?.find((p) => p.id === postId);
            if (updatedPost && updatedPost.status !== 'regenerating') {
              clearInterval(pollInterval);
              setRegeneratingPost(null);
            }
          }
        } catch (err) {
          console.error('Error polling regeneration:', err);
          clearInterval(pollInterval);
          setRegeneratingPost(null);
        }
      }, 3000);
      // Safety timeout: stop polling after 60s
      setTimeout(() => {
        clearInterval(pollInterval);
        setRegeneratingPost(null);
        fetchCurrentBatch();
      }, 60000);
    } catch (error) {
      notify.error(error.response?.data?.detail || t('weeklyPosts.regenerateError'));
      setRegeneratingPost(null);
    }
  };

  const handleGenerateVideoSingle = async (postId) => {
    if (!(await confirmAsync({ message: t('weeklyPosts.generateVideoConfirm') }))) return;

    setGeneratingVideoPost(postId);
    try {
      await apiClient.post(`/api/v1/posts/posts/${postId}/generate-video`);
      fetchCurrentBatch();
    } catch (error) {
      notify.error(error.response?.data?.detail || t('weeklyPosts.generateVideoError'));
    } finally {
      setGeneratingVideoPost(null);
    }
  };

  // Edit published Facebook caption
  const handleSaveCaption = async (postId) => {
    const newCaption = captionText.trim();
    if (!newCaption) return;

    console.log('[CAPTION_EDIT] Sending PUT /posts/' + postId + '/caption', {
      caption: newCaption.substring(0, 80) + '...',
      captionLength: newCaption.length,
    });

    setSavingCaption(true);
    try {
      const res = await apiClient.put(`/api/v1/posts/posts/${postId}/caption`, {
        caption: newCaption,
      });
      console.log('[CAPTION_EDIT] Success response:', res.data);
      setEditingCaption(null);
      setCaptionText('');
      fetchScheduledPosts(); // Refresh to show updated caption
    } catch (err) {
      console.error('[CAPTION_EDIT] Error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
      });
      const detail = err.response?.data?.detail || t('comments.captionEditError');
      notify.error(detail);
    } finally {
      setSavingCaption(false);
    }
  };


  // Delete a published post from Facebook / Instagram
  const handleDeletePost = async (postId, post) => {
    // Build a confirmation message that calls out the IG/TikTok soft-delete caveat
    // — the post will be removed from the CRM but stays live on those platforms.
    const platforms = post?.target_platforms || [];
    const willStayOnIg = platforms.includes('instagram') && !!post?.instagram_media_id;
    const willStayOnTt = platforms.includes('tiktok') && !!post?.tiktok_video_id;
    let confirmMessage = t('comments.confirmDeletePost');
    if (willStayOnIg || willStayOnTt) {
      const extras = [];
      if (willStayOnIg) extras.push(t('singlePost.instagramDeleteUnsupported'));
      if (willStayOnTt) extras.push(t('singlePost.tiktokDeleteUnsupported'));
      confirmMessage = `${confirmMessage}\n\n${extras.join('\n')}`;
    }
    if (!(await confirmAsync({ message: confirmMessage, danger: true }))) return;

    setDeletingPost(postId);
    try {
      const res = await apiClient.delete(`/api/v1/posts/posts/${postId}`);
      // Show partial-success warning when some platforms failed
      if (res.data?.errors?.length) {
        notify.warning(`${t('comments.postDeleted')}\n⚠️ ${res.data.errors.join('; ')}`);
      }
      fetchScheduledPosts();
    } catch (err) {
      const detail = err.response?.data?.detail || t('comments.deletePostError');
      notify.error(detail);
    } finally {
      setDeletingPost(null);
    }
  };

  // Try to bind an orphan-approved post to a publication slot.
  // Used by the "Try to schedule" button next to the orphan badge.
  const handleRetryScheduling = async (postId) => {
    setRetryingScheduling(postId);
    try {
      const res = await apiClient.post(`/api/v1/posts/posts/${postId}/retry-scheduling`);
      // Refresh the batch (orphan badge → queued badge on success).
      fetchCurrentBatch();
      // If still orphan, surface the message so the user understands.
      if (!res.data?.scheduled && res.data?.status_message) {
        notify.info(res.data.status_message);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || t('weeklyPosts.tooltips.notScheduled');
      notify.error(detail);
    } finally {
      setRetryingScheduling(null);
    }
  };

  // Promote a scheduled / approved post to immediate publication.
  //
  // The Celery worker takes ~30-60s to publish a Reel across all target
  // platforms (download from S3 → IG resumable upload → TikTok upload →
  // FB Reel 3-phase upload → poll until ready). To reflect the live
  // status without requiring a manual page reload, we poll
  // ``/api/v1/posts/posts`` every 2s after the publish-now call and
  // stop as soon as the post reaches a terminal status
  // (``published`` / ``partially_published`` / ``failed``) or after the
  // 180s safety cap. A toast banner is shown above the list with the
  // outcome, so the user has clear feedback even if the post pill is
  // off-screen.
  const handlePublishNow = async (postId) => {
    if (!(await confirmAsync({ message: t('singlePost.confirmPublishNow') }))) return;

    // Auto-switch to the "all" filter so the post stays visible across
    // every status transition (approved → queued → publishing →
    // published / partially_published / failed). Without this, a user
    // who clicks Publish-Now from "Approved" would see the post vanish
    // as soon as its status changes.
    setScheduledFilter('all');

    setPublishingNowPost(postId);
    setPublishNotification({
      type: 'info',
      message: t('singlePost.publishNowInProgress'),
    });

    try {
      await apiClient.post(`/api/v1/posts/posts/${postId}/publish-now`);
      // Immediate refresh shows the queued/publishing pill right away.
      await fetchScheduledPosts();

      const pollInterval = setInterval(async () => {
        try {
          const res = await apiClient.get('/api/v1/posts/posts');
          const posts = res.data || [];
          setScheduledPosts(posts);
          const updated = posts.find((p) => p.id === postId);
          if (updated && PUBLISH_TERMINAL_STATUSES.includes(updated.status)) {
            clearInterval(pollInterval);
            setPublishingNowPost(null);

            // Build the result toast.
            let toastType = 'success';
            let toastMessage = t('singlePost.publishNowSuccess');
            if (updated.status === 'partially_published') {
              toastType = 'partial';
              const failed = Object.entries(updated.platform_results || {})
                .filter(([, v]) => v !== 'published')
                .map(([k]) => PLATFORM_LABELS[k] || k);
              toastMessage = t('singlePost.publishNowPartial', {
                failed: failed.join(', ') || '—',
              });
            } else if (updated.status === 'failed') {
              toastType = 'error';
              toastMessage = updated.status_message
                ? t('singlePost.publishNowFailedDetail', { detail: updated.status_message })
                : t('singlePost.publishNowFailed');
            }
            setPublishNotification({ type: toastType, message: toastMessage });
            // Auto-dismiss success/partial toasts; keep errors until the
            // user navigates away or starts another publish-now.
            if (toastType !== 'error') {
              setTimeout(() => {
                setPublishNotification((current) =>
                  current && current.message === toastMessage ? null : current
                );
              }, 8000);
            }
          }
        } catch (pollErr) {
          console.error('Error polling publish-now status:', pollErr);
          clearInterval(pollInterval);
          setPublishingNowPost(null);
        }
      }, 2000);

      // Safety cap — clear the spinner even if the worker never reports
      // a terminal state (network blip, log loss, manual DB edit).
      setTimeout(() => {
        clearInterval(pollInterval);
        setPublishingNowPost((current) => (current === postId ? null : current));
        fetchScheduledPosts();
      }, 180000);
    } catch (err) {
      const detail = err.response?.data?.detail || t('singlePost.publishNowError');
      setPublishNotification({ type: 'error', message: detail });
      setPublishingNowPost(null);
    }
  };

  // Load the per-platform capability matrix once on mount.
  const fetchCapabilities = async () => {
    try {
      const res = await apiClient.get('/api/v1/posts/capabilities');
      setCapabilities(res.data);
    } catch (err) {
      // Non-fatal — UI falls back to enabling all actions if the call fails.
      console.error('Error fetching post capabilities:', err);
    }
  };

  // True iff at least one of the post's target platforms supports caption editing.
  // In v1 this means: Facebook is among target_platforms AND a Facebook id is present.
  const canEditCaption = (post) => {
    return Boolean(post.facebook_video_id || post.facebook_post_id);
  };

  // Build a tooltip that explains why edit-caption is disabled for IG/TikTok-only posts.
  const captionEditTooltip = (post) => {
    if (canEditCaption(post)) return t('comments.editCaption');
    const platforms = post.target_platforms || [];
    const messages = [];
    if (platforms.includes('instagram')) {
      messages.push(t('singlePost.instagramEditUnsupported'));
    }
    if (platforms.includes('tiktok')) {
      messages.push(t('singlePost.tiktokEditUnsupported'));
    }
    return messages.length > 0
      ? messages.join(' ')
      : t('comments.captionEditNotSupported');
  };

  const hasAnyPlatform = hasInstagram || hasTiktok || hasFacebook;

  // Prerequisites check
  if (!loading && (!hasAnyPlatform || !hasVoiceClone)) {
    return (
      <div className="weekly-posts-page">
        <div className="title-with-back">
          <button className="back-button" onClick={() => navigate('/dashboard')} title={t('common.back_to_dashboard')}>
            <i className="bi bi-arrow-left"></i>
          </button>
          <h2>{t('weeklyPosts.prerequisitesTitle')}</h2>
        </div>
        <div className="prerequisites-warning">
          {!hasAnyPlatform && (
            <div className="prereq-item missing">
              <span className="icon">❌</span>
              <span>{t('weeklyPosts.needAtLeastOnePlatform')}</span>
            </div>
          )}
          {!hasVoiceClone && (
            <div className="prereq-item missing">
              <span className="icon">❌</span>
              <span>{t('weeklyPosts.needVoiceClone')}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  const isDraft = batch?.status === 'draft';
  const isGenerating = batch && ['confirmed', 'generating'].includes(batch.status);
  const isGeneratingContent = batch?.status === 'generating_content';
  const isFailed = batch?.status === 'failed';

  // ── Helper: format date for scheduled list ──
  const formatPostDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return {
      day: d.getDate(),
      month: d.toLocaleString(undefined, { month: 'short' }),
      full: d.toLocaleString(),
    };
  };

  const getStatusPillClass = (status) => {
    if (['audio_failed', 'video_failed', 'failed'].includes(status)) return 'pill-failed';
    if (status === 'partially_published') return 'pill-partially_published';
    return `pill-${status}`;
  };

  // ── Render: Scheduled Posts Tab ──
  const renderScheduledTab = () => {
    if (scheduledLoading) {
      return <div className="loading">{t('common.loading')}</div>;
    }

    return (
      <div className="scheduled-posts-section">
        {publishNotification && (
          <div
            className={`publish-notification publish-notification-${publishNotification.type}`}
            role="status"
            aria-live="polite"
          >
            <span className="publish-notification-icon">
              {publishNotification.type === 'success' && '✅'}
              {publishNotification.type === 'partial' && '⚠️'}
              {publishNotification.type === 'error' && '❌'}
              {publishNotification.type === 'info' && '⏳'}
            </span>
            <span className="publish-notification-message">
              {publishNotification.message}
            </span>
            <button
              type="button"
              className="publish-notification-close"
              aria-label={t('common.close')}
              onClick={() => setPublishNotification(null)}
            >
              ×
            </button>
          </div>
        )}
        <div className="scheduled-posts-filters">
          {['all', 'approved', 'queued', 'published', 'failed'].map((f) => (
            <button
              key={f}
              className={`filter-btn ${scheduledFilter === f ? 'active' : ''}`}
              onClick={() => setScheduledFilter(f)}
            >
              {t(`weeklyPosts.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
            </button>
          ))}
        </div>

        {filteredScheduledPosts.length === 0 ? (
          <div className="scheduled-posts-empty">
            <div className="empty-icon">📭</div>
            <p>{t('weeklyPosts.scheduledEmpty')}</p>
          </div>
        ) : (
          filteredScheduledPosts.map((post) => {
            const schedDate = formatPostDate(post.scheduled_publish_at || post.scheduled_date);
            return (
              <div key={post.id} className="scheduled-post-card">
                {schedDate && (
                  <div className="scheduled-post-date">
                    <span className="date-day">{schedDate.day}</span>
                    <span className="date-month">{schedDate.month}</span>
                  </div>
                )}
                <div className="scheduled-post-body">
                  <div className="post-meta">
                    <span className={`status-pill ${getStatusPillClass(post.status)}`}>
                      {t(`weeklyPosts.postStatus.${post.status}`, post.status)}
                    </span>
                    <span className="platform-icons">
                      {post.target_platforms?.map((p) => {
                        const result = post.platform_results?.[p];
                        const isOk = result === 'published';
                        const isFail = result && result !== 'published';
                        return (
                          <span
                            key={p}
                            title={`${PLATFORM_LABELS[p] || p}: ${isOk ? t('weeklyPosts.postStatus.published') : isFail ? parsePlatformResult(result) : t('weeklyPosts.platformPending')}`}
                            className={`platform-icon-badge ${isOk ? 'platform-ok' : isFail ? 'platform-fail' : ''}`}
                          >
                            {PLATFORM_ICONS[p] || p}
                          </span>
                        );
                      })}
                    </span>
                  </div>
                  {post.caption && (
                    <p className="caption-text">{post.caption}</p>
                  )}

                  {/* Caption-level controls: edit button + per-platform
                      capability badges. Placed RIGHT BELOW the caption
                      (instead of in the bottom action row) so the user
                      sees the edit affordance and the per-platform
                      support state next to the text they would edit. */}
                  {['published', 'partially_published'].includes(post.status) && editingCaption !== post.id && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '0.4rem',
                        margin: '0.25rem 0 0.5rem',
                      }}
                    >
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                        disabled={!canEditCaption(post)}
                        onClick={() => {
                          if (!canEditCaption(post)) return;
                          setEditingCaption(post.id);
                          setCaptionText(post.caption || '');
                        }}
                        title={captionEditTooltip(post)}
                      >
                        ✏️ {t('singlePost.edit')}
                      </button>
                      {capabilities && (post.target_platforms || []).map((p) => (
                        <PlatformCapabilityBadge
                          key={`${p}-edit-inline`}
                          platform={p}
                          capabilities={capabilities}
                          action="edit"
                          compact
                        />
                      ))}
                    </div>
                  )}

                  {/* Inline Caption Edit Form (Facebook only) */}
                  {editingCaption === post.id && (
                    <div className="pcm-edit-row" style={{ margin: '0.5rem 0' }}>
                      <textarea
                        className="pcm-reply-input"
                        value={captionText}
                        onChange={(e) => setCaptionText(e.target.value)}
                        rows={3}
                        style={{ flex: 1, resize: 'vertical' }}
                      />
                      <button
                        className="pcm-reply-btn"
                        disabled={savingCaption || !captionText.trim()}
                        onClick={() => handleSaveCaption(post.id)}
                      >
                        {savingCaption ? '...' : t('common.save')}
                      </button>
                      <button
                        className="pcm-cancel-btn"
                        onClick={() => { setEditingCaption(null); setCaptionText(''); }}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  )}

                  {post.hashtags?.length > 0 && (
                    <div className="hashtags">
                      {post.hashtags.map((tag, i) => (
                        <span key={i} className="hashtag">#{tag}</span>
                      ))}
                    </div>
                  )}

                  {/* Per-platform error details for partially published / failed posts */}
                  {(post.status === 'partially_published' || post.status === 'failed') && post.platform_results && (
                    <div className="platform-results-detail">
                      {Object.entries(post.platform_results).map(([platform, result]) => {
                        const isOk = result === 'published';
                        const errorMsg = parsePlatformResult(result);
                        return (
                          <div key={platform} className={`platform-result-row ${isOk ? 'result-ok' : 'result-error'}`}>
                            <span className="platform-result-icon">
                              {PLATFORM_ICONS[platform] || platform}
                            </span>
                            <span className="platform-result-name">
                              {PLATFORM_LABELS[platform] || platform}
                            </span>
                            {isOk ? (
                              <span className="platform-result-status ok">✓ {t('weeklyPosts.postStatus.published')}</span>
                            ) : (
                              <span className="platform-result-status error">
                                ✗ {t(`weeklyPosts.platformErrors.${result?.split(':')[0]?.trim()}`, errorMsg || t('weeklyPosts.platformErrors.generic'))}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="scheduled-post-actions">
                  {post.video_url && (
                    <a
                      href={post.video_url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-download-video"
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                      title={t('comments.downloadVideo')}
                    >
                      <i className="bi bi-download"></i> {t('comments.downloadVideo')}
                    </a>
                  )}
                  {post.instagram_permalink && (
                    <a
                      href={post.instagram_permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                      title={t('comments.viewOnInstagram')}
                    >
                      📸 {t('comments.viewOnInstagram')}
                    </a>
                  )}
                  {post.tiktok_share_url && (
                    <a
                      href={post.tiktok_share_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                      title={t('comments.viewOnTikTok')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.7a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.13z"/>
                      </svg> {t('comments.viewOnTikTok')}
                    </a>
                  )}
                  {post.facebook_permalink && (
                    <a
                      href={post.facebook_permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                      title={t('comments.viewOnFacebook')}
                    >
                      📘 {t('comments.viewOnFacebook')}
                    </a>
                  )}
                  {post.status === 'published' && post.instagram_media_id && (
                    <button
                      className="comment-count-badge"
                      onClick={() => setCommentsModal({ postId: post.id, caption: post.caption, aiPaused: !!post.ai_replies_paused })}
                      title={t('comments.igComments')}
                    >
                      📸💬 {commentCounts[post.id] || 0}
                    </button>
                  )}
                  {post.status === 'published' && (post.facebook_video_id || post.facebook_post_id) && (
                    <button
                      className="comment-count-badge fb"
                      onClick={() => setFbCommentsModal({ postId: post.id, caption: post.caption, aiPaused: !!post.ai_replies_paused })}
                      title={t('comments.fbComments')}
                    >
                      📘💬 {fbCommentCounts[post.id] || 0}
                    </button>
                  )}
                  {/* Publish-now: promote a scheduled/approved post to immediate publication */}
                  {PUBLISH_NOW_PROMOTABLE_STATUSES.includes(post.status) && post.video_url && (
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                      disabled={publishingNowPost === post.id}
                      onClick={() => handlePublishNow(post.id)}
                      title={t('singlePost.publishNow')}
                    >
                      {publishingNowPost === post.id
                        ? '...'
                        : `🚀 ${t('singlePost.publishNow')}`}
                    </button>
                  )}
                  {/* Edit-caption button moved next to the caption text
                      above (see "Caption-level controls"). The action
                      row keeps only the destructive delete + the
                      delete-capability badges. */}
                  {['published', 'partially_published'].includes(post.status) && (
                    <button
                      className="btn btn-danger"
                      style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                      disabled={deletingPost === post.id}
                      onClick={() => handleDeletePost(post.id, post)}
                      title={t('comments.deletePost')}
                    >
                      {deletingPost === post.id ? '...' : `🗑️ ${t('singlePost.delete')}`}
                    </button>
                  )}
                  {['published', 'partially_published'].includes(post.status) && capabilities && (
                    <span style={{ display: 'inline-flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {(post.target_platforms || []).map((p) => (
                        <PlatformCapabilityBadge
                          key={`${p}-delete`}
                          platform={p}
                          capabilities={capabilities}
                          action="delete"
                          compact
                        />
                      ))}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // ── Render: Generation Tab ──
  const renderGenerateTab = () => {
    // No batch yet — show generate button
    if (!batch) {
      return (
        <div className="generate-section">
          <p>{t('weeklyPosts.description')}</p>

          {/* Platform Selector */}
          <div className="platform-selector">
            <h3>{t('weeklyPosts.platformTitle')}</h3>
            <p className="platform-subtitle">{t('weeklyPosts.platformDescription')}</p>
            <div className="platform-toggles">
              <button
                type="button"
                className={`platform-toggle ${selectedPlatforms.includes('instagram') ? 'selected' : ''} ${!hasInstagram ? 'disabled' : ''}`}
                onClick={() => hasInstagram && togglePlatform('instagram')}
                disabled={!hasInstagram}
                title={!hasInstagram ? t('weeklyPosts.platformNotConnected') : 'Instagram'}
              >
                <span className="platform-toggle-icon">📸</span>
                <span className="platform-toggle-label">Instagram</span>
                {!hasInstagram && <span className="platform-toggle-status">⚠️</span>}
              </button>
              <button
                type="button"
                className={`platform-toggle ${selectedPlatforms.includes('tiktok') ? 'selected' : ''} ${!hasTiktok ? 'disabled' : ''}`}
                onClick={() => hasTiktok && togglePlatform('tiktok')}
                disabled={!hasTiktok}
                title={!hasTiktok ? t('weeklyPosts.platformNotConnected') : 'TikTok'}
              >
                <span className="platform-toggle-icon">🎵</span>
                <span className="platform-toggle-label">TikTok</span>
                {!hasTiktok && <span className="platform-toggle-status">⚠️</span>}
              </button>
              <button
                type="button"
                className={`platform-toggle ${selectedPlatforms.includes('facebook') ? 'selected' : ''} ${!hasFacebook ? 'disabled' : ''}`}
                onClick={() => hasFacebook && togglePlatform('facebook')}
                disabled={!hasFacebook}
                title={!hasFacebook ? t('weeklyPosts.platformNotConnected') : 'Facebook'}
              >
                <span className="platform-toggle-icon">📘</span>
                <span className="platform-toggle-label">Facebook</span>
                {!hasFacebook && <span className="platform-toggle-status">⚠️</span>}
              </button>
            </div>
          </div>

          {/* Target Audience Selector */}
          <div className="target-selector">
            <h3>{t('weeklyPosts.targetTitle')}</h3>
            <p className="target-subtitle">{t('weeklyPosts.targetDescription')}</p>
            <div className="target-checkboxes">
              {Object.entries(availableTargets).map(([key, target]) => (
                <label
                  key={key}
                  className={`target-checkbox ${selectedTargets.includes(key) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedTargets.includes(key)}
                    onChange={() => toggleTarget(key)}
                  />
                  <span className="target-icon">{t(`weeklyPosts.targetIcons.${key}`)}</span>
                  <span className="target-info">
                    <span className="target-label">{t(`weeklyPosts.targets.${key}`)}</span>
                    <span className="target-desc">{t(`weeklyPosts.targetDescs.${key}`)}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Active Tone Display (read-only) */}
          <div className="tone-selector">
            <h3>{t('weeklyPosts.toneTitle')}</h3>
            {activeArchetype ? (
              <div className="active-archetype-row">
                <span className="active-archetype-icon">{activeArchetype.icon}</span>
                <div>
                  <span className="active-archetype-label">{activeArchetype.label_it}</span>
                  <span className="active-archetype-tagline">{activeArchetype.tagline_it}</span>
                </div>
                <button
                  className="btn btn-link btn-sm"
                  onClick={() => navigate('/profile')}
                  type="button"
                >
                  {t('weeklyPosts.changeTone', 'Cambia')}
                </button>
              </div>
            ) : (
              <p className="tone-subtitle">
                {t('weeklyPosts.toneNotSet', 'Nessun tono selezionato. Vai al profilo per impostarlo.')}
                <button className="btn btn-link btn-sm" onClick={() => navigate('/profile')} type="button">
                  {t('weeklyPosts.changeTone', 'Imposta ora')}
                </button>
              </p>
            )}
          </div>

          <button
            className="btn btn-primary btn-large"
            onClick={handleGenerateWeek}
            disabled={generating}
          >
            {generating ? t('weeklyPosts.generating') : t('weeklyPosts.generateButton')}
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="page-header">
          <div className="batch-info">
            <span className="week-label">
              {t('weeklyPosts.weekOf')} {new Date(batch.week_start_date).toLocaleDateString()}
            </span>
            <span className={`status-badge status-${batch.status}`}>
              {t(`weeklyPosts.status.${batch.status}`)}
            </span>
            {batch.content_tone && (
              <span className="tone-badge">
                {batch.content_tone}
              </span>
            )}
          </div>
          {isDraft && (
            <div className="batch-actions">
              <button
                className="btn btn-regenerate-all"
                onClick={handleRegenerateAll}
                disabled={regeneratingAll || regeneratingPost}
              >
                {regeneratingAll ? (
                  <><span className="spinner small" /> {t('weeklyPosts.regeneratingAll')}</>
                ) : (
                  <><i className="bi bi-arrow-clockwise"></i> {t('weeklyPosts.regenerateAll')}</>
                )}
              </button>
              <button
                className="btn btn-delete-batch"
                onClick={handleDeleteBatch}
                disabled={regeneratingAll || regeneratingPost}
              >
                <i className="bi bi-trash3"></i> {t('weeklyPosts.deleteBatch')}
              </button>
            </div>
          )}
        </div>

        {/* Posts Grid */}
        <div className="posts-grid">
          {batch.posts.map((post) => {
            const dayAbbr = t(`days.${DAYS[post.day_of_week]}`).substring(0, 3);
            const isProcessing = ['generating_audio', 'generating_video'].includes(post.status);
            const hasFailed = ['audio_failed', 'video_failed', 'failed'].includes(post.status);

            return (
              <div key={post.id} className={`post-card status-${post.status}`}>
                {/* IG-style header */}
                <div className="post-header">
                  <div className="post-day-avatar">{dayAbbr}</div>
                  <div className="post-header-info">
                    <h3>{t(`days.${DAYS[post.day_of_week]}`)}</h3>
                    {post.scheduled_date && (
                      <span className="post-date-sub">
                        {new Date(post.scheduled_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="post-header-right">
                    {post.target_audience && (
                      <span className="target-badge">
                        {t(`weeklyPosts.targets.${post.target_audience}`) || post.target_audience}
                      </span>
                    )}
                    {post.user_edited && (
                      <span className="edited-badge">{t('weeklyPosts.edited')}</span>
                    )}
                    {post.video_url && (
                      <a
                        href={post.video_url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-header-download"
                        title={t('weeklyPosts.downloadVideo')}
                      >
                        <i className="bi bi-download"></i>
                      </a>
                    )}
                  </div>
                </div>

                {editingPost === post.id ? (
                  <div style={{ padding: '1rem' }}>
                    <PostEditForm
                      post={post}
                      onSave={(updates) => handleSaveEdit(post.id, updates)}
                      onCancel={() => setEditingPost(null)}
                    />
                  </div>
                ) : (
                  <>
                    {/* Hero media area */}
                    {post.video_url ? (
                      <div className="post-media-area">
                        <video
                          controls
                          preload="metadata"
                          className="video-player"
                        >
                          <source src={post.video_url} type="video/mp4" />
                          {t('weeklyPosts.videoNotSupported')}
                        </video>
                      </div>
                    ) : (
                      <div className="post-script-hero">
                        <p className="script-display">
                          {post.script_text || '...'}
                        </p>
                        <span className="script-icon">🎬</span>
                        {isProcessing && (
                          <div className="post-status-overlay">
                            <span className="spinner" />
                            <div className="status-indicator">
                              {post.status === 'generating_audio'
                                ? t('weeklyPosts.generatingAudio')
                                : t('weeklyPosts.generatingVideo')}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Caption area */}
                    <div className="post-body">
                      {post.caption && (
                        <p className="caption-text">
                          <span className="caption-label">{dayAbbr}</span>
                          {post.caption}
                        </p>
                      )}
                      {post.hashtags?.length > 0 && (
                        <div className="hashtags">
                          {post.hashtags.map((tag, i) => (
                            <span key={i} className="hashtag">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Failed actions */}
                    {hasFailed && (
                      <div className="post-failed-actions">
                        <span className="error-message">
                          ❌ {t(`weeklyPosts.postStatus.${post.status}`)}
                        </span>
                        <button
                          className="btn btn-retry"
                          onClick={() => handleGenerateVideoSingle(post.id)}
                          disabled={generatingVideoPost === post.id}
                        >
                          {generatingVideoPost === post.id ? (
                            <><span className="spinner small" /> {t('weeklyPosts.retryingVideo')}</>
                          ) : (
                            <><i className="bi bi-arrow-clockwise"></i> {t('weeklyPosts.retryVideo')}</>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Status badges */}
                    {post.status === 'approved' && (
                      <div className="post-status-badge approved">
                        ✅ {t('weeklyPosts.postStatus.approved')}
                      </div>
                    )}
                    {post.is_orphan_approved && (
                      <div
                        className="post-status-badge orphan"
                        title={t('weeklyPosts.tooltips.notScheduled')}
                      >
                        ⚠️ {t('weeklyPosts.postStatus.notScheduled')}
                        <button
                          className="btn btn-retry-scheduling"
                          onClick={() => handleRetryScheduling(post.id)}
                          disabled={retryingScheduling === post.id}
                          title={t('weeklyPosts.tooltips.notScheduled')}
                        >
                          {retryingScheduling === post.id ? (
                            <><span className="spinner small" /> {t('weeklyPosts.regenerating')}</>
                          ) : (
                            <><i className="bi bi-arrow-clockwise"></i> {t('weeklyPosts.actions.retryScheduling')}</>
                          )}
                        </button>
                      </div>
                    )}
                    {post.status === 'queued' && (
                      <div className="post-status-badge queued">
                        📅 {t('weeklyPosts.postStatus.queued')}
                        {post.scheduled_publish_at && (
                          <span className="publish-time">
                            {' '}{new Date(post.scheduled_publish_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                    {post.status === 'rejected' && (
                      <div className="post-status-badge rejected">
                        ❌ {t('weeklyPosts.postStatus.rejected')}
                      </div>
                    )}

                    {/* Action footer */}
                    {isDraft && (
                      <div className="post-card-footer">
                        <button
                          className="btn btn-secondary"
                          onClick={() => setEditingPost(post.id)}
                          disabled={regeneratingPost === post.id}
                        >
                          <i className="bi bi-pencil"></i> {t('common.edit')}
                        </button>
                        <button
                          className="btn btn-regenerate"
                          onClick={() => handleRegenerateSingle(post.id)}
                          disabled={regeneratingPost !== null || regeneratingAll}
                        >
                          {regeneratingPost === post.id ? (
                            <><span className="spinner small" /> {t('weeklyPosts.regenerating')}</>
                          ) : (
                            <><i className="bi bi-arrow-clockwise"></i> {t('weeklyPosts.regenerateSingle')}</>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Confirm Button */}
        {isDraft && (
          <div className="confirm-section">
            <p className="confirm-note">{t('weeklyPosts.confirmNote')}</p>
            <button
              className="btn btn-primary btn-large"
              onClick={handleConfirmBatch}
              disabled={confirming}
            >
              {confirming ? t('common.processing') : t('weeklyPosts.confirmWeek')}
            </button>
          </div>
        )}

        {isGenerating && (
          <div className="generation-banner">
            <div className="generation-banner-header">
              <div className="generation-banner-title">
                <span className="spinner small" />
                <h3>{t('weeklyPosts.generatingContent')}</h3>
              </div>
              <p className="generation-banner-subtitle">{t('weeklyPosts.generatingContentDesc')}</p>
            </div>

            {generationProgress && (
              <div className="generation-progress">
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-fill"
                    style={{
                      width: `${Math.round(
                        ((generationProgress.summary.audio_done +
                          generationProgress.summary.video_done) /
                          (generationProgress.total_posts * 2)) *
                          100
                      )}%`,
                    }}
                  />
                </div>
                <div className="progress-stats">
                  <span>🎙️ {t('weeklyPosts.progressAudio')}: {generationProgress.summary.audio_done}/{generationProgress.total_posts}</span>
                  <span>🎬 {t('weeklyPosts.progressVideo')}: {generationProgress.summary.video_done}/{generationProgress.total_posts}</span>
                  {generationProgress.summary.failed > 0 && (
                    <span className="progress-failed">❌ {t('weeklyPosts.progressFailed')}: {generationProgress.summary.failed}</span>
                  )}
                </div>
                <div className="progress-posts-detail">
                  {generationProgress.posts.map((p) => (
                    <div key={p.id} className={`progress-post-item status-${p.status}`}>
                      <span className="day-label">{t(`days.${DAYS[p.day_of_week]}`)}</span>
                      <span className="post-status-icon">
                        {p.status === 'generating_audio' && '🎙️'}
                        {p.status === 'generating_video' && '🎬'}
                        {p.status === 'approved' && '✅'}
                        {(p.status === 'audio_failed' || p.status === 'video_failed') && '❌'}
                        {p.status === 'draft' && '⏳'}
                      </span>
                      <span className="post-status-text">
                        {t(`weeklyPosts.postStatus.${p.status}`, p.status)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {isGeneratingContent && (
          <>
            <div className="skeleton-generating-header">
              <div className="skeleton-gen-info">
                <div className="skeleton-gen-title">
                  <span className="spinner small" />
                  <h3>{t('weeklyPosts.generatingDrafts')}</h3>
                </div>
                <p className="skeleton-gen-subtitle">{t('weeklyPosts.generatingDraftsDesc')}</p>
              </div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleDeleteBatch}
              >
                {t('weeklyPosts.cancelGeneration')}
              </button>
            </div>
            <div className="posts-grid">
              {DAYS.map((day, i) => (
                <div key={day} className="skeleton-post-card" style={{ animationDelay: `${i * 0.08}s` }}>
                  {/* Skeleton header — IG style */}
                  <div className="skeleton-card-header">
                    <div className="skeleton-line skeleton-avatar" />
                    <div className="skeleton-header-text">
                      <div className="skeleton-line skeleton-header" />
                      <div className="skeleton-line skeleton-sub" />
                    </div>
                  </div>
                  {/* Skeleton media area */}
                  <div className="skeleton-line skeleton-media" />
                  {/* Skeleton caption */}
                  <div className="skeleton-caption-area">
                    <div className="skeleton-line skeleton-text-long" />
                    <div className="skeleton-line skeleton-text-medium" />
                    <div className="skeleton-tags">
                      <div className="skeleton-line skeleton-tag" />
                      <div className="skeleton-line skeleton-tag" />
                      <div className="skeleton-line skeleton-tag" />
                    </div>
                  </div>
                  {/* Skeleton action footer */}
                  <div className="skeleton-btn-row">
                    <div className="skeleton-line skeleton-btn" />
                    <div className="skeleton-line skeleton-btn" />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {isFailed && (
          <div className="failed-notice">
            <p>⚠️ {t('weeklyPosts.failedNotice')}</p>
            <button
              className="btn btn-primary"
              onClick={handleDeleteBatch}
            >
              {t('weeklyPosts.deleteAndRetry')}
            </button>
          </div>
        )}
      </>
    );
  };

  // ── Main Render ──
  return (
    <div className="weekly-posts-page">
      <div className="title-with-back">
        <button className="back-button" onClick={() => navigate('/dashboard')} title={t('common.back_to_dashboard')}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <h1>{t('weeklyPosts.title')}</h1>
      </div>

      {/* Tab Navigation */}
      <div className="posts-tabs">
        <button
          className={`posts-tab-btn ${activeTab === 'generate' ? 'active' : ''}`}
          onClick={() => setActiveTab('generate')}
        >
          <span className="tab-icon">✨</span>
          {t('weeklyPosts.tabGenerate')}
        </button>
        <button
          className={`posts-tab-btn ${activeTab === 'scheduled' ? 'active' : ''}`}
          onClick={() => setActiveTab('scheduled')}
        >
          <span className="tab-icon">📅</span>
          {t('weeklyPosts.tabScheduled')}
          {scheduledCount > 0 && (
            <span className="tab-count">{scheduledCount}</span>
          )}
        </button>
      </div>

      {activeTab === 'generate' && renderGenerateTab()}
      {activeTab === 'scheduled' && renderScheduledTab()}

      {/* Instagram Comment Modal */}
      {commentsModal && (
        <PostComments
          postId={commentsModal.postId}
          postCaption={commentsModal.caption}
          initialAiPaused={commentsModal.aiPaused}
          onClose={() => setCommentsModal(null)}
        />
      )}

      {/* Facebook Comment Modal */}
      {fbCommentsModal && (
        <FBPostComments
          postId={fbCommentsModal.postId}
          postCaption={fbCommentsModal.caption}
          initialAiPaused={fbCommentsModal.aiPaused}
          onClose={() => setFbCommentsModal(null)}
        />
      )}
    </div>
  );
}

export default Posts;
